import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, useMediaQuery } from '@mui/material';
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { StatementsRecord } from '../api/salary/statements.service';
import { getItem } from '../helper/localstorage.helper';
import './../scss/components/invoice-table-detailed.style.scss';

interface StatementsInvoiceSectionProps {
    statements: StatementsRecord | null;
    loading: boolean;
    error?: string | null;
}

type ComponentTone = 'gross' | 'net' | 'tax' | 'social' | 'faktura';

interface BreakdownRow {
    key: ComponentTone;
    label: string;
    value?: number;
}

export const StatementsInvoiceSection: React.FC<StatementsInvoiceSectionProps> = ({
    statements,
    loading,
    error,
}) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const language = getItem('i18nextLng') || 'en-US';
    const currency = t('kr');

    const fmt = (n?: number) =>
        new Intl.NumberFormat(language.toLowerCase().includes('sv') ? 'sv-SE' : 'en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(Number(n) || 0);

    const rows: BreakdownRow[] = statements
        ? [
            { key: 'gross', label: t('GROSS_SALARY_BEFORE_TAX', { defaultValue: 'Gross Salary (before tax)' }), value: statements.grossSalary },
            { key: 'social', label: t('EMPLOYER_SOCIAL_CONTRIBUTIONS', { defaultValue: 'Employer Social Security Contributions' }), value: statements.socialFee },
        ].filter((row) => row.value !== undefined && row.value !== null)
        : [];

    return (
        <div className="invoice-table-container statements-table">
            <div className="invoice-table-content">
                <div className="invoice-table">
                    {!isMobile && (
                        <TableHeader>
                            <TableColumn>{t('COMPONENT', { defaultValue: 'Component' })}</TableColumn>
                            <TableColumn>{t('AMOUNT') || 'Amount'}</TableColumn>
                        </TableHeader>
                    )}

                    <TableBody>
                        {loading ? (
                            // Skeleton rows — violet shimmer (styled via .statements-v2 .MuiSkeleton-root)
                            Array.from({ length: 4 }).map((_, r) => (
                                <div className="st-skeleton__row" key={`sk-${r}`}>
                                    <Skeleton variant="text" width="55%" height={18} animation="wave" />
                                    <Skeleton variant="rounded" width={92} height={26} animation="wave" />
                                </div>
                            ))
                        ) : statements === null ? (
                            <div className="mi-empty-state">
                                <div className="mi-empty-state__icon" aria-hidden="true">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 2h6a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6l4-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                        <path d="M9 2v4h4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                        <path d="M9 14h6M9 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <h4 className="mi-empty-state__title">{t('NO_DATA_FOUND')}</h4>
                                <p className="mi-empty-state__message">
                                    {error || t('NO_INCOME_HINT', { defaultValue: 'No income records found for the selected year. Try a different year.' })}
                                </p>
                            </div>
                        ) : isMobile ? (
                            // Mobile: card-based layout
                            rows.map((row) => (
                                <MobileCardWrapper key={row.key}>
                                    <MobileCardRow label={t('COMPONENT', { defaultValue: 'Component' })}>
                                        <span className="st-component">
                                            <span className={`st-dot st-dot--${row.key}`} />
                                            {row.label}
                                        </span>
                                    </MobileCardRow>
                                    <MobileCardRow label={t('AMOUNT') || 'Amount'}>
                                        <span className="mi-amount">{fmt(row.value)} {currency}</span>
                                    </MobileCardRow>
                                </MobileCardWrapper>
                            ))
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell>
                                        <span className="st-component">
                                            <span className={`st-dot st-dot--${row.key}`} />
                                            {row.label}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="mi-amount">{fmt(row.value)} {currency}</span>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </div>
            </div>
        </div>
    );
};
